import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

interface BookshelfState {
  favorites: string[];
  readStories: string[];
  toggleFavorite: (storyId: string) => void;
  markAsRead: (storyId: string) => void;
  isFavorite: (storyId: string) => boolean;
  isRead: (storyId: string) => boolean;
}

const BookshelfContext = createContext<BookshelfState | null>(null);

export function BookshelfProvider({ children }: { children: ReactNode }) {
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("ww-favorites") || "[]");
    } catch {
      return [];
    }
  });

  const [readStories, setReadStories] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("ww-read") || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("ww-favorites", JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    localStorage.setItem("ww-read", JSON.stringify(readStories));
  }, [readStories]);

  const toggleFavorite = (storyId: string) => {
    setFavorites((prev) =>
      prev.includes(storyId)
        ? prev.filter((id) => id !== storyId)
        : [...prev, storyId]
    );
  };

  const markAsRead = (storyId: string) => {
    setReadStories((prev) =>
      prev.includes(storyId) ? prev : [...prev, storyId]
    );
  };

  const isFavorite = (storyId: string) => favorites.includes(storyId);
  const isRead = (storyId: string) => readStories.includes(storyId);

  return (
    <BookshelfContext.Provider
      value={{ favorites, readStories, toggleFavorite, markAsRead, isFavorite, isRead }}
    >
      {children}
    </BookshelfContext.Provider>
  );
}

export function useBookshelf() {
  const ctx = useContext(BookshelfContext);
  if (!ctx) throw new Error("useBookshelf must be used within BookshelfProvider");
  return ctx;
}
